import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { errorMessage, formatRelativeTime } from '../lib/utils';
import { getUnreadNotificationCount, listMyNotifications, markAllNotificationsRead, markNotificationRead } from '../lib/api';
import type { NotificationItem } from '../types';

function notificationCopy(notification: NotificationItem): string {
  switch (notification.event_type) {
    case 'COHOST_ADDED': return 'Anda telah dilantik sebagai Co-Host';
    case 'COHOST_REMOVED': return 'Peranan anda sebagai Co-Host telah ditamatkan';
    case 'EXPECTED_ATTENDEE_ADDED': return 'Anda dijemput menghadiri mesyuarat';
    case 'MEETING_UPDATED': return 'Butiran mesyuarat telah dikemas kini';
    case 'MEETING_CANCELLED': return 'Mesyuarat telah dibatalkan';
  }
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(async () => {
    try { setUnreadCount(await getUnreadNotificationCount()); } catch { /* Header count is non-critical. */ }
  }, []);

  useEffect(() => {
    void refreshCount();
    const timer = window.setInterval(() => { if (!document.hidden) void refreshCount(); }, 30000);
    return () => window.clearInterval(timer);
  }, [refreshCount]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', escape); };
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next) return;
    setBusy(true);
    try { setItems(await listMyNotifications()); await refreshCount(); }
    catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setBusy(false); }
  };

  const openNotification = async (notification: NotificationItem) => {
    try {
      if (!notification.read_at) {
        await markNotificationRead(notification.notification_id);
        setItems((current) => current.map((item) => item.notification_id === notification.notification_id ? { ...item, read_at: new Date().toISOString() } : item));
        setUnreadCount((current) => Math.max(0, current - 1));
      }
      setOpen(false);
      navigate(`/meetings/${notification.meeting_id}`);
    } catch (error) { showToast(errorMessage(error), 'error'); }
  };

  const markAllRead = async () => {
    if (!unreadCount) return;
    try {
      await markAllNotificationsRead();
      setItems((current) => current.map((item) => item.read_at ? item : { ...item, read_at: new Date().toISOString() }));
      setUnreadCount(0);
    } catch (error) { showToast(errorMessage(error), 'error'); }
  };

  return <div className="notification-bell-wrap" ref={panelRef}>
      <button type="button" className={`notification-bell${open ? ' is-open' : ''}`} aria-label={unreadCount ? `${unreadCount} notifikasi belum dibaca` : 'Buka notifikasi'} aria-expanded={open} onClick={() => void toggle()}>
      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" strokeLinecap="round" strokeLinejoin="round" /></svg>
      {unreadCount > 0 ? <span className="notification-count" aria-hidden="true">{unreadCount > 99 ? '99+' : unreadCount}</span> : null}
    </button>
    {open ? <section className="notification-panel" aria-label="Notifikasi" role="dialog">
      <div className="notification-panel-head"><div><h2>Notifikasi</h2><p>{unreadCount ? `${unreadCount} belum dibaca` : 'Semua telah dibaca'}</p></div><button type="button" className="button button-ghost button-small" onClick={() => void markAllRead()} disabled={!unreadCount}>Tandakan semua sebagai dibaca</button></div>
      {busy ? <p className="notification-empty">Memuatkan notifikasi…</p> : items.length ? <div className="notification-list">
        {items.map((item) => <button type="button" className={`notification-item${item.read_at ? '' : ' is-unread'}`} key={item.notification_id} onClick={() => void openNotification(item)}>
          <span className="notification-item-dot" aria-hidden="true" />
          <span className="notification-item-copy"><strong>{notificationCopy(item)}</strong><span className="notification-title" title={item.meeting_title_snapshot}>{item.meeting_title_snapshot}</span><small>{formatRelativeTime(item.created_at)}</small></span>
        </button>)}
      </div> : <p className="notification-empty">Tiada notifikasi baharu.</p>}
    </section> : null}
  </div>;
}
